import React, { useState, useEffect } from "react";
import {
  Building2,
  Search,
  ExternalLink,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  Database,
  Users,
  Home,
  Receipt,
  FileText,
  AlertOctagon,
  CheckCircle,
  Key,
  Clock,
  PlayCircle,
  X,
  Eye,
  Edit3,
  Shield,
} from "lucide-react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import CompanyDetailDrawer from "../components/CompanyDetailDrawer";

export default function CompaniesTab() {
  const [companies, setCompanies] = useState([]);
  const [supportRequests, setSupportRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Modal for suspend / activate confirmation
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    company: null,
    targetStatus: "suspended",
  });

  // Modal for Requesting Delegated Support Access
  const [requestModal, setRequestModal] = useState({
    open: false,
    company: null,
    requestedRole: "viewer",
    durationHours: 1,
    reason: "",
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [compRes, reqRes] = await Promise.all([
        api.get("/super-admin/companies"),
        api.get("/super-admin/support-requests"),
      ]);
      setCompanies(compRes.data || []);
      setSupportRequests(reqRes.data || []);
    } catch (err) {
      toast.error("Failed to load customer companies and support requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getSupportRequestForCompany = (accountId) => {
    const matching = supportRequests.filter((r) => r.account_id === accountId);
    const approved = matching.find((r) => r.status === "approved");
    if (approved) return approved;
    const pending = matching.find((r) => r.status === "pending");
    if (pending) return pending;
    return matching[0] || null;
  };

  const handleCreateSupportRequest = async () => {
    if (!requestModal.company) return;
    if (!requestModal.reason.trim() || requestModal.reason.trim().length < 10) {
      toast.error("Please provide a reason with at least 10 characters");
      return;
    }
    setBusyId(requestModal.company.id);
    try {
      const res = await api.post("/super-admin/support-requests", {
        account_id: requestModal.company.id,
        requested_role: requestModal.requestedRole,
        duration_hours: Number(requestModal.durationHours),
        reason: requestModal.reason.trim(),
      });
      if (res.data?.ok) {
        toast.success(`Support access requested for '${requestModal.company.name}'. Awaiting owner approval.`);
        setRequestModal({ open: false, company: null, requestedRole: "viewer", durationHours: 1, reason: "" });
        const reqRes = await api.get("/super-admin/support-requests");
        setSupportRequests(reqRes.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit support request");
    } finally {
      setBusyId(null);
    }
  };

  const handleCancelSupportRequest = async (reqId) => {
    setBusyId(reqId);
    try {
      const res = await api.delete(`/super-admin/support-requests/${reqId}`);
      if (res.data?.ok) {
        toast.success("Support request cancelled");
        const reqRes = await api.get("/super-admin/support-requests");
        setSupportRequests(reqRes.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel request");
    } finally {
      setBusyId(null);
    }
  };

  const handleEnterSupportSession = async (supportReq) => {
    setBusyId(supportReq.id);
    try {
      const res = await api.post(`/super-admin/support-requests/${supportReq.id}/enter`);
      if (res.data?.ok) {
        toast.success(`Entering support session for '${res.data.company_name}' (${res.data.role.toUpperCase()})`);
        window.location.href = "/";
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to enter support session");
    } finally {
      setBusyId(null);
    }
  };

  const executeStatusChange = async () => {
    const { company, targetStatus } = confirmModal;
    if (!company) return;

    setBusyId(company.id);
    try {
      const res = await api.post(`/super-admin/companies/${company.id}/status`, {
        status: targetStatus,
      });
      if (res.data?.ok) {
        toast.success(
          `Company '${company.name}' marked as ${targetStatus.toUpperCase()}`
        );
        setCompanies((prev) =>
          prev.map((c) => (c.id === company.id ? { ...c, status: targetStatus } : c))
        );
        setSelectedCompany((prev) =>
          prev && prev.id === company.id ? { ...prev, status: targetStatus } : prev
        );
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to update company status";
      toast.error(msg);
    } finally {
      setBusyId(null);
      setConfirmModal({ open: false, company: null, targetStatus: "suspended" });
    }
  };

  const filteredCompanies = companies.filter((c) => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchName = c.name?.toLowerCase().includes(q);
      const matchId = c.id?.toLowerCase().includes(q);
      const matchSub = c.subdomain?.toLowerCase().includes(q);
      return matchName || matchId || matchSub;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-400" />
            <span>Customer Companies & Tenants</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Monitor isolated tenant database sizes, active users, and control company access.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-teal-400" : ""}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Search and Filter Pills */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-2xl bg-slate-900/60 border border-slate-800">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company name, ID, or subdomain..."
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-950/80 border border-slate-700/60 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </div>

        <div className="flex items-center gap-1.5 self-start sm:self-center">
          {["all", "active", "suspended"].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors ${
                statusFilter === st
                  ? "bg-teal-500/20 text-teal-300 border border-teal-500/40"
                  : "bg-slate-950/50 text-slate-400 hover:text-white border border-transparent"
              }`}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* Companies Table / List */}
      <div className="bg-slate-900/70 border border-slate-800 rounded-2xl shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                <th className="py-3 px-4">Company Account</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Usage & Storage</th>
                <th className="py-3 px-4">Entities</th>
                <th className="py-3 px-4">Created Date</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500 text-xs">
                    {loading ? "Loading customer companies..." : "No companies match the filter criteria."}
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((c) => {
                  const isSuspended = c.status === "suspended";
                  const isBusy = busyId === c.id;

                  return (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedCompany(c)}
                      className="hover:bg-slate-800/60 transition-colors group cursor-pointer"
                      title="Click to view company details"
                    >
                      {/* Name & ID */}
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-white text-sm flex items-center gap-2">
                          <span>{c.name}</span>
                          {c.id === "default" && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold tracking-wider bg-teal-500/10 text-teal-400 border border-teal-500/30">
                              PRIMARY
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                          ID: {c.id} {c.subdomain ? `• @${c.subdomain}` : ""}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4">
                        {isSuspended ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-rose-500/15 text-rose-400 border border-rose-500/30">
                            <ShieldAlert className="w-3 h-3" /> Suspended
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                            <ShieldCheck className="w-3 h-3" /> Active
                          </span>
                        )}
                      </td>

                      {/* Storage */}
                      <td className="py-3.5 px-4 space-y-1">
                        <div className="flex items-center gap-1.5 text-slate-300">
                          <Database className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                          <span className="font-semibold">{c.db_size_formatted || "0 KB"}</span>
                          <span className="text-slate-500 font-mono text-[10px]">(sqlite)</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-400 text-[11px]">
                          <FileText className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                          <span>{c.storage_size_formatted || "0 KB"} uploads</span>
                        </div>
                      </td>

                      {/* Entities */}
                      <td className="py-3.5 px-4 space-y-1">
                        <div className="flex items-center gap-3 text-[11px]">
                          <span className="flex items-center gap-1 text-slate-300">
                            <Home className="w-3.5 h-3.5 text-teal-400" />
                            {c.properties_count || 0} props
                          </span>
                          <span className="flex items-center gap-1 text-slate-300">
                            <Users className="w-3.5 h-3.5 text-blue-400" />
                            {c.users_count || 0} users
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[11px] text-slate-400">
                          <Receipt className="w-3.5 h-3.5 text-indigo-400" />
                          {c.transactions_count || 0} transactions
                        </div>
                      </td>

                      {/* Created At */}
                      <td className="py-3.5 px-4 text-slate-400 text-[11px] whitespace-nowrap">
                        {c.created_at ? c.created_at.split("T")[0] : "—"}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          {/* Delegated Support Access Button */}
                          {(() => {
                            const req = getSupportRequestForCompany(c.id);
                            if (req?.status === "approved") {
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEnterSupportSession(req);
                                  }}
                                  disabled={isBusy}
                                  title={`Enter support mode (${req.requested_role.toUpperCase()}) - Approved by Owner`}
                                  className="px-2.5 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 font-bold text-xs flex items-center gap-1.5 transition-all shadow-sm animate-pulse disabled:opacity-50"
                                >
                                  <PlayCircle className="w-3.5 h-3.5 text-emerald-400" />
                                  <span>Enter Support</span>
                                </button>
                              );
                            }
                            if (req?.status === "pending") {
                              return (
                                <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <span
                                    title={`Requested ${req.requested_role.toUpperCase()} for ${req.duration_hours}h. Reason: ${req.reason}`}
                                    className="px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] font-bold tracking-wide uppercase flex items-center gap-1"
                                  >
                                    <Clock className="w-3 h-3 animate-spin" />
                                    <span>Pending Owner</span>
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelSupportRequest(req.id);
                                    }}
                                    disabled={isBusy}
                                    title="Cancel pending support request"
                                    className="p-1 rounded-md text-slate-500 hover:text-rose-400 hover:bg-slate-800 transition-colors"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRequestModal({
                                    open: true,
                                    company: c,
                                    requestedRole: "viewer",
                                    durationHours: 1,
                                    reason: "",
                                  });
                                }}
                                disabled={isBusy || isSuspended}
                                title="Request temporary authorized support access from the company owner"
                                className="px-2.5 py-1.5 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/30 text-teal-400 hover:text-teal-300 font-bold text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
                              >
                                <Key className="w-3.5 h-3.5" />
                                <span>Request Access</span>
                              </button>
                            );
                          })()}

                          {/* Suspend / Reactivate button */}
                          {isSuspended ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmModal({
                                  open: true,
                                  company: c,
                                  targetStatus: "active",
                                });
                              }}
                              disabled={isBusy}
                              className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-semibold flex items-center gap-1 transition-colors disabled:opacity-50"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                              <span>Reactivate</span>
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmModal({
                                  open: true,
                                  company: c,
                                  targetStatus: "suspended",
                                });
                              }}
                              disabled={isBusy}
                              className="px-2.5 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 text-xs font-semibold flex items-center gap-1 transition-colors disabled:opacity-50"
                            >
                              <AlertOctagon className="w-3.5 h-3.5" />
                              <span>Suspend</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-slate-800 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  confirmModal.targetStatus === "suspended"
                    ? "bg-rose-500/20 text-rose-400"
                    : "bg-emerald-500/20 text-emerald-400"
                }`}
              >
                <AlertOctagon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">
                  {confirmModal.targetStatus === "suspended" ? "Suspend Account Access?" : "Reactivate Company Account?"}
                </h3>
                <p className="text-xs text-slate-400">
                  Target Company: <span className="text-white font-semibold">{confirmModal.company?.name}</span>
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              {confirmModal.targetStatus === "suspended"
                ? "Suspending this company will immediately prevent any user under this tenant from accessing their portal or APIs (returning HTTP 403 Suspended). Existing database files and transactions remain preserved."
                : "Reactivating this account will restore full access for all registered company users immediately."}
            </p>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                onClick={() => setConfirmModal({ open: false, company: null, targetStatus: "suspended" })}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeStatusChange}
                disabled={busyId != null}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                  confirmModal.targetStatus === "suspended"
                    ? "bg-rose-600 hover:bg-rose-500 text-white"
                    : "bg-emerald-600 hover:bg-emerald-500 text-white"
                }`}
              >
                Confirm {confirmModal.targetStatus === "suspended" ? "Suspension" : "Activation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Delegated Support Access Modal */}
      {requestModal.open && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">
                    Request Support Access
                  </h3>
                  <p className="text-xs text-slate-400">
                    Tenant Owner Authorization Required
                  </p>
                </div>
              </div>
              <button
                onClick={() => setRequestModal({ open: false, company: null, requestedRole: "viewer", durationHours: 1, reason: "" })}
                className="text-slate-500 hover:text-slate-300 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Target Company Banner */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/70 border border-slate-800/80">
              <div>
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Target Portfolio</span>
                <div className="text-sm font-bold text-white">{requestModal.company?.name}</div>
              </div>
              <div className="text-right">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Account ID</span>
                <div className="text-xs font-mono text-slate-400">{requestModal.company?.id}</div>
              </div>
            </div>

            {/* Role / Permission Tier Selection */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-300 block">
                Requested Permission Tier
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setRequestModal((prev) => ({ ...prev, requestedRole: "viewer" }))}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    requestModal.requestedRole === "viewer"
                      ? "bg-teal-500/15 border-teal-500/50 text-teal-300 shadow-sm"
                      : "bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700"
                  }`}
                >
                  <div className="flex items-center gap-1.5 font-bold text-xs">
                    <Eye className="w-4 h-4" />
                    <span>Read-Only (Viewer)</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 leading-normal">
                    Inspect properties, ledgers, rent roll & diagnostics. Cannot create or edit data.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setRequestModal((prev) => ({ ...prev, requestedRole: "editor" }))}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    requestModal.requestedRole === "editor"
                      ? "bg-blue-500/15 border-blue-500/50 text-blue-300 shadow-sm"
                      : "bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700"
                  }`}
                >
                  <div className="flex items-center gap-1.5 font-bold text-xs">
                    <Edit3 className="w-4 h-4" />
                    <span>Troubleshoot & Edit</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1 leading-normal">
                    Update transactions, fix broken records, recalculate balances, and resolve issues.
                  </p>
                </button>
              </div>
            </div>

            {/* Duration Selection */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-300 block">
                Session Duration Window
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[1, 4, 12, 24].map((hrs) => (
                  <button
                    key={hrs}
                    type="button"
                    onClick={() => setRequestModal((prev) => ({ ...prev, durationHours: hrs }))}
                    className={`py-2 rounded-xl text-xs font-bold transition-all ${
                      requestModal.durationHours === hrs
                        ? "bg-teal-500 text-slate-950 shadow-md font-extrabold"
                        : "bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700"
                    }`}
                  >
                    {hrs} {hrs === 1 ? "Hour" : "Hours"}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500">
                Session automatically expires when time window ends. Owner can revoke at any time.
              </p>
            </div>

            {/* Reason Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-300">
                  Troubleshooting Reason <span className="text-rose-400">*</span>
                </label>
                <span className={`text-[10px] font-mono ${requestModal.reason.length >= 10 ? "text-teal-400" : "text-amber-400"}`}>
                  {requestModal.reason.length}/10 min chars
                </span>
              </div>
              <textarea
                value={requestModal.reason}
                onChange={(e) => setRequestModal((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Explain why support access is needed (e.g., investigating lease sync discrepancy reported by client)..."
                rows={3}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950/80 border border-slate-700/60 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setRequestModal({ open: false, company: null, requestedRole: "viewer", durationHours: 1, reason: "" })}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateSupportRequest}
                disabled={busyId != null || requestModal.reason.trim().length < 10}
                className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-500 text-white text-xs font-bold transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                <Key className="w-3.5 h-3.5" />
                <span>Send Request to Owner</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slide-over Company Details Drawer */}
      <CompanyDetailDrawer
        company={selectedCompany}
        open={Boolean(selectedCompany)}
        onClose={() => setSelectedCompany(null)}
        supportRequest={selectedCompany ? getSupportRequestForCompany(selectedCompany.id) : null}
        onEnterSupport={handleEnterSupportSession}
        onRequestSupport={(comp) => {
          setRequestModal({
            open: true,
            company: comp,
            requestedRole: "viewer",
            durationHours: 1,
            reason: "",
          });
        }}
        onToggleStatus={(comp, targetStatus) => {
          setConfirmModal({
            open: true,
            company: comp,
            targetStatus,
          });
        }}
        onRefresh={loadData}
      />
    </div>
  );
}
